import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ApiEndpoints } from '@app/utils/enums/api-endpoints';
import { getFilledParams } from '@app/utils/helpers/request.helpers';
import {
  OrdersQueryParams,
  OrderPaginatedResponse,
  OrderResponse,
  OrderHistoryResponse,
  OrderStatusData,
  OrderStatusResponse,
  OrderStatusHistoryResponse,
  Order,
} from '@app/utils/interfaces/order.interface';
import { environment } from '@env/environment';
import { CustomerPaginatedResponse } from '@app/utils/interfaces/customer.interface';
import { CreateOrderPayload } from '@app/utils/interfaces/orders.interface';

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly http = inject(HttpClient);

  public getOrders(params?: OrdersQueryParams): Observable<OrderPaginatedResponse> {
    return this.http.get<OrderPaginatedResponse>(`${this.baseUrl}${ApiEndpoints.GET_ORDERS}`, {
      params: getFilledParams(params),
    });
  }

  public getDistinctCreators(pageNumber = 1, pageSize = 10000): Observable<string[]> {
    return this.getOrders({ pageNumber, pageSize }).pipe(
      map((response: OrderPaginatedResponse) => {
        const creators = new Set<string>();
        response.data.items.forEach((item: Order) => {
          creators.add(item.creator);
        });
        return Array.from(creators);
      }),
    );
  }

  public getOrderById(id: string): Observable<OrderResponse> {
    return this.http.get<OrderResponse>(
      `${this.baseUrl}${ApiEndpoints.GET_ORDERS}/${id}/information`,
    );
  }

  public getOrderHistory(customerId: string): Observable<OrderHistoryResponse> {
    return this.http.get<OrderHistoryResponse>(
      `${this.baseUrl}${ApiEndpoints.GET_ORDERS_HISTORY}/${customerId}`,
    );
  }
  public printOrder(id: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}${ApiEndpoints.PRINT_ORDER}/${id}`, {
      responseType: 'blob',
    });
  }

  public getOrderStatus(orderId: number): Observable<OrderStatusHistoryResponse> {
    return this.http.get<OrderStatusHistoryResponse>(
      `${this.baseUrl}${ApiEndpoints.EDIT_ORDER_STATUS}/${orderId}`,
    );
  }

  public updateOrderStatus(orderStatusData: OrderStatusData): Observable<OrderStatusResponse> {
    const params = {
      OrderId: orderStatusData.OrderId.toString(),
      Status: orderStatusData.Status.toString(),
      Comment: orderStatusData.Comment ?? '',
    };

    return this.http.post<OrderStatusResponse>(
      `${this.baseUrl}${ApiEndpoints.EDIT_ORDER_STATUS}`,
      {},
      { params },
    );
  }

  public searchCustomers(searchTerm = ''): Observable<CustomerPaginatedResponse> {
    const params = new HttpParams().set('Search', searchTerm);
    return this.http.get<CustomerPaginatedResponse>(
      `${this.baseUrl}${ApiEndpoints.GET_CUSTOMERS}`,
      { params },
    );
  }

  public createOrder(orderPayload: CreateOrderPayload): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}${ApiEndpoints.POST_ORDERS}`, orderPayload);
  }
}
